import { ChartContainer, ChartTooltip, ChartTooltipContent } from 'components/ui/chart';
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts';
import { formatReportNumber } from '../reportCells';
import { formatWhole } from './chartFormat';

export type ReportBar = {
    key: string;
    /** The axis label under the bar. */
    label: string;
    /** The bar's name in its tooltip, when it says more than the axis label. */
    tooltipLabel?: string;
    value: number;
};

type Props = {
    bars: ReportBar[];
    /** What the bars measure, such as "Billable hours": the tooltip's line and the chart's accessible name. */
    measure: string;
    /** Decimals of the figure in the tooltip; the value labels on the bars are whole numbers. */
    decimals?: number;
};

/** Past this many bars the value labels would collide, and the tooltip carries the figures alone. */
const MAX_LABELLED_BARS = 24;

/**
 * One measure as vertical bars, oldest first, each with its whole value on top and its exact value in the
 * tooltip. A single series, so it has no legend: the panel's title names it.
 */
export function ReportBarChart({ bars, measure, decimals = 2 }: Props) {
    // One hue for the one series; its value labels and tooltip text stay in the ink tokens.
    const config = { value: { label: measure, color: 'var(--chart-4)' } };
    return (
        <ChartContainer config={config} role="img" aria-label={measure} className="aspect-auto h-72 w-full">
            <BarChart data={bars} accessibilityLayer={false} margin={{ top: 24, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} interval="preserveStartEnd" />
                <YAxis
                    tickLine={false}
                    axisLine={false}
                    width={64}
                    tickFormatter={(value: number) => formatWhole(value)}
                    className="tabular-nums"
                />
                <ChartTooltip
                    cursor={false}
                    content={
                        <ChartTooltipContent
                            hideIndicator
                            labelFormatter={(_, payload) => {
                                const bar = payload?.[0]?.payload as ReportBar | undefined;
                                return bar?.tooltipLabel ?? bar?.label;
                            }}
                            formatter={(value) => (
                                <span className="flex w-full justify-between gap-4">
                                    <span className="text-muted-foreground">{measure}</span>
                                    <span className="text-foreground font-medium tabular-nums">
                                        {formatReportNumber(Number(value), decimals)}
                                    </span>
                                </span>
                            )}
                        />
                    }
                />
                <Bar
                    dataKey="value"
                    fill="var(--color-value)"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={56}
                    isAnimationActive={false}
                >
                    {bars.length <= MAX_LABELLED_BARS && (
                        <LabelList
                            dataKey="value"
                            position="top"
                            offset={6}
                            // Recharts wraps a label at spaces to fit the bar's width; a no-break space keeps the
                            // thousands separator from splitting the figure over two lines.
                            formatter={(value) => formatWhole(Number(value)).replace(/ /g, '\u00a0')}
                            className="fill-foreground text-caption tabular-nums"
                        />
                    )}
                </Bar>
            </BarChart>
        </ChartContainer>
    );
}
